import { datadogRum } from '@datadog/browser-rum';
import $ from 'jquery';
import Moment from 'moment-timezone';
import { marked } from 'marked';
import 'bootstrap';
import 'svgxuse/svgxuse';
import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/browser';
import get from 'lodash/get';
import moment from 'moment';

import { enrichUnhandledExceptionData, sentryBeforeSendProcessing } from '~/helpers/exceptionHandler';
import { flashMessages } from '~/helpers/messages';
import { ajaxFetch, convertToCamel } from '~/helpers/api';
import { routes, getRoute } from '~/helpers/routes';
import SessionSecurity from '~/helpers/sessions';
import { initializeI18n } from '~/helpers/i18n';
import { openZendeskSupportForm } from '~/helpers/zendesk';
import { boot as bootIntercom, isIntercomAvailable } from '~/helpers/intercom';
import { kebabToCaps } from '~/helpers/formatting';

export const initializeObservabilitySuite = ({
  environment,
  sentryDsn,
  datadogRumAppId,
  datadogRumClientToken,
}) => {
  let releaseVersion = '';

  if (!/development|test|darkprod|local/.test(environment)) {
    if (process.env.APP_VERSION) {
      releaseVersion = `dashboard-frontend@${process.env.APP_VERSION}`;
    }

    datadogRum.init({
      applicationId: datadogRumAppId,
      clientToken: datadogRumClientToken,
      site: 'datadoghq.com',
      service: 'daffy',
      env: environment,
      version: releaseVersion,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100, // Combined with the manual recording below to allow precise customer based feature flag overrides
      startSessionReplayRecordingManually: true,
      trackResources: true,
      trackFrustrations: true,
      trackUserInteractions: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask',
      allowedTracingUrls: [/https:\/\/(?!www\.).*\.alloy\.(co|com)/], // Exclude `www.` CMS subdomain to avoid CORS issues with DD headers
      beforeSend: event => {
        // Custom triggered actions (`datadogRum.addAction`) passed through unaltered
        if (event.action?.type === 'custom') {
          return true;
        }

        // Auto actions with names prefixed with `alloydd-` should be pre sent in dd rum. These should not cause PII leaks
        if (event?.action?.target?.name?.startsWith('alloydd-')) {
          // eslint-disable-next-line no-unsafe-optional-chaining
          const [, name] = event?.action?.target?.name.split('alloydd-');
          event.action.target.name = name;
        } else if (event?.action?.target?.name) {
          // remove target with potential PII from event
          event.action.target.name = '';
        }

        return true;
      },
    });

    /** Percentage of sessions to record replay for (between 0-100) */
    const datadogReplaySampleRate = 1;

    // Allows for random session replay recordings that can later be enforced via customer based feature flag
    if (Math.floor(Math.random() * 101) <= datadogReplaySampleRate) {
      datadogRum.startSessionReplayRecording();
    }

    Sentry.init({
      dsn: sentryDsn,
      integrations: [
        browserTracingIntegration(),
      ],
      release: releaseVersion,
      tracesSampleRate: 0,
      environment,
      debug: !environment.includes('prod'),
      ignoreErrors: [
        'Non-Error',
        'The user aborted a request',
        'ResizeObserver loop',
        'Unknown option clicked',
      ],
      beforeSend(event, hint) {
        return sentryBeforeSendProcessing(event, hint);
      },
    });

    Sentry.addEventProcessor(event => {
      if (event?.exception?.values?.[0]?.mechanism) {
        const exception = event.exception.values[0];
        const mechanism = exception?.mechanism;

        if ((mechanism?.type === 'instrument' && mechanism?.data?.target === 'EventTarget')
          && (exception?.value?.match(/Cannot read|is not a function|is undefined/i) && exception?.type === TypeError)) {
          event.tags.user_action = 'event';
        } else if (event.contexts?.trace?.op === 'pageload') {
          event.tags.user_action = 'visit_page';
        }

        if (mechanism.handled === false || mechanism.type === 'instrument' || !event?.tags?.enriched) {
          const enrichmentData = enrichUnhandledExceptionData(event);

          event.tags = { ...event.tags, ...enrichmentData.tags };
          event.level = enrichmentData.level;
        }
      }

      return event;
    });
  }
};

export const configureObservabilitySuiteContexts = ({
  agent,
  customer,
  environment,
  featureFlags = {},
}) => {
  if (agent) {
    const {
      firstName,
      lastName,
      email,
      isAgent,
      isEmployee,
      ...agentUserData
    } = convertToCamel(agent);
    const { organizationNameSlug } = convertToCamel(customer);
    const {
      datadogRecordAllSessions = false,
    } = convertToCamel(featureFlags);

    const agentCustomerOrgSlug = isEmployee
      ? organizationNameSlug
      : agentUserData?.customerAgents?.[0]?.organizationNameSlug;
    const agentFullName = `${firstName}${lastName ? ` ${lastName}` : ''}${isEmployee ? ' (Alloy)' : ''}`;

    if (!/development|test|darkprod|local/.test(environment)) {
      Sentry.setUser({
        email,
        organization_name_slug: agentCustomerOrgSlug,
        isAlloyAdmin: isEmployee,
      });

      let releaseVersion = '';
      if (process.env.APP_VERSION) {
        releaseVersion = process.env.APP_VERSION;
      }

      // datadog cross integration purposes
      Sentry.setTag('organization_name_slug', agentCustomerOrgSlug);
      Sentry.setTag('env', environment);
      Sentry.setTag('version', releaseVersion);
      Sentry.setTag('isAlloyAdmin', isEmployee);

      datadogRum.setUser({
        email,
        agentFullName,
        agentCustomerOrgSlug,
        isAlloyAdmin: isEmployee,
      });

      // Were already recording an explicit percent of user sessions.
      // This allows for a specific customer's feature flags to force their session to be recorded
      if (datadogRecordAllSessions) {
        datadogRum.startSessionReplayRecording();
      }
    }
  }
};

export const initializeZendesk = () => {
  if (window?.document && window.zE) {
    window.zE(() => { window.zE.hide(); });
  }
};

/**
 * Boots whichever support widget the page actually shipped, and only that one.
 *
 * The dashboard template renders the Intercom snippet when the app ID is set and the
 * intercom-messenger-chat flag is on, and the Zendesk snippet otherwise -- never both.
 * Keying off which global exists means the flag is evaluated once, server side, and the
 * two can't disagree. Both widgets hide their own launcher; Support is entered from the
 * sidenav either way.
 */
export const initializeSupportWidget = () => {
  if (isIntercomAvailable()) {
    bootIntercom();
  } else {
    initializeZendesk();
  }
};

export function highlightSidebar() {
  let finished = false;

  function makeLinkActive($link) {
    const linkUrl = $link.find('a').attr('href');
    const reviewPage = /\/entities\/[PBE]-[a-zA-Z0-9]{12,}\/applications\/[a-zA-Z0-9]{32,}\/review\//;
    const isReviewQueue = linkUrl
      && linkUrl.includes('queues')
      && window.location.pathname.match(reviewPage);

    if (isReviewQueue || window.location.pathname.indexOf(linkUrl) > -1) {
      $link.addClass('active');
      finished = true;
    }
  }

  // Set sidebar tab to be active on page load
  if ($('li.queues-link')) makeLinkActive($('li.queues-link'));
  const $sidebarLinks = $('nav#sidebar > ul.components > li:not(.expanded-toggle)');
  $sidebarLinks.each(linkIdx => {
    if (!finished) makeLinkActive($sidebarLinks.eq(linkIdx));
  });
}


function convertMarkdown() {
  $('.convert-markdown').each((index, el) => {
    const $el = $(el);
    $el.html(marked($el.text()));
  });
}


function checkTimezone() {
  const sessionTzOffset = +document.getElementById('current_tz_offset').value;
  const browserTzOffset = new Date().getTimezoneOffset() * -1;
  const guessedTz = Moment.tz.guess();
  if (sessionTzOffset !== browserTzOffset) {
    // If the session timezone if different from the current browser timezone, update
    // the session timezone via the session data
    ajaxFetch(getRoute('sessionData'), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tz_offset: browserTzOffset, tz: guessedTz }),
    });
  }
}

function selectCellBaseAppUrlForTld(cellBaseAppUrls) {
  const mapping = {};
  for (const url of cellBaseAppUrls) {
    const tld = url.split('.').slice(-1)[0];
    mapping[tld] = url;
  }

  const currentTld = window.location.origin.split('.').slice(-1)[0];
  if (currentTld in mapping) {
    return mapping[currentTld];
  }
  return cellBaseAppUrls[0];
}

export async function initializePage(isSPA = false) {
  const authState = await ajaxFetch(routes.authentication.state, { camelCase: true });
  const {
    alloyEnvironment,
    cellBaseAppUrl,
    datadogRumAppId,
    datadogRumClientToken,
    sentryDsn,
    isAuthenticated,
  } = authState;

  initializeObservabilitySuite({
    environment: alloyEnvironment,
    sentryDsn,
    datadogRumAppId,
    datadogRumClientToken,
  });
  initializeSupportWidget();
  await initializeI18n();

  /**
   * Background: devops has to manually update the google console with newly created customer vanity
   * urls (e.g organization-slug.app.alloy.com) to support successful google auth flow
   * This section of code eliminates the manual process by redirecting to the vanity url's true canonical
   * domain. e.g organiazation-slug.app.alloy.com may map to alloy-us.app.alloy.com.
   */
  const cellBaseAppUrls = cellBaseAppUrl ? cellBaseAppUrl.split(',') : [];
  const onVanityDomain = cellBaseAppUrls?.length
    ? !cellBaseAppUrls.includes(window.location.origin) : false;

  const authRoutes = [
    '/mfa/',
    '/password_reset', '/reset-password', '/password_change',
    '/verify_email', '/verify-email',
    '/verify_customer_email', '/verify-customer-email',
    '/invitation',
  ];
  const isUserOnAuthRoute = authRoutes.some(path => window.location.pathname.includes(path));

  const isNeitherOnAuthOrOnAuthRoute = !isAuthenticated && !isUserOnAuthRoute;
  const redirectToLoginOnVanity = window.location.pathname.includes('/login') || isNeitherOnAuthOrOnAuthRoute;
  const isVanityUrlLogin = onVanityDomain && redirectToLoginOnVanity;
  const hasVanityRedirectParam = new URLSearchParams(window.location.search).get('vanityDomainRedirect') === 'true';

  if (isVanityUrlLogin && !hasVanityRedirectParam) {
    const encodeVanityUrl = `vanity_url=${encodeURIComponent(window.location.origin)}`;
    const selectedCellBaseAppUrl = selectCellBaseAppUrlForTld(cellBaseAppUrls);
    window.location.replace(
      `${selectedCellBaseAppUrl}/v3/dashboard/login?${encodeVanityUrl}`,
    );
  }

  const $sideNav = $('nav#sidebar');
  const $collapsers = $('div.container-fluid, ul#messages');

  const expandCollapseSidebar = expanded => {
    if (expanded) {
      $sideNav.addClass('expanded');
      $collapsers.removeClass('expanded');
    } else {
      $sideNav.removeClass('expanded');
      $collapsers.addClass('expanded');
    }
  };

  const isSidebarExpanded = localStorage.getItem('sidebarExpanded');
  expandCollapseSidebar(JSON.parse(isSidebarExpanded));

  function toggleSidebar() {
    const updatedIsSidebarExpanded = localStorage.getItem('sidebarExpanded');
    const expanded = JSON.parse(updatedIsSidebarExpanded);
    function updateSession(sidebarExpanded) {
      localStorage.setItem('sidebarExpanded', sidebarExpanded);
    }
    expandCollapseSidebar(!expanded);
    updateSession(!expanded);
  }

  if (!isSPA) {
    // fetch user data & initialize user session tracking
    const data = await ajaxFetch(routes.v3.userContext);
    const agentData = get(data, 'agent');
    const customerData = get(data, 'customer');
    const customerAccessRecord = get(get(data, 'admin_customer_access'), get(customerData, 'organization_name_slug'));
    const adminPermissions = get(data, 'admin_permissions');
    const featureFlags = get(data, 'feature_flags');

    if (agentData && customerData) {
      configureObservabilitySuiteContexts({
        agent: agentData,
        customer: customerData,
        environment: alloyEnvironment,
        featureFlags,
      });
    }

    $('#sidebar #sidebar-support-form-cta').on('click',
      () => {
        const sidebarExpanded = $('nav#sidebar').hasClass('expanded');
        openZendeskSupportForm(
          sidebarExpanded,
          {
            firstName: agentData.first_name,
            lastName: agentData.last_name,
            email: agentData.email,
          },
        );
      });

    if (agentData.is_employee) {
      $('#customer-view-page-notice').css('display', 'flex');
      $('#customer-view-page-notice').children().css('display', 'flex');
      $('#customer-view-page-notice').children().css('justify-content', 'center');
      $('#org-name-env-text').text(`${kebabToCaps(alloyEnvironment)} - ${customerData.organization_name}`);
      if (customerAccessRecord && customerAccessRecord.expired_at && !adminPermissions['READ:Customers:null']) {
        $('#temp-access-expiration-timestamp').text(moment(customerAccessRecord.expired_at).format('M/D/YY h:mmA'));
      } else {
        $('#temp-access-expiration-icon').css('display', 'none');
        $('#temp-access-expiration-timestamp').css('display', 'none');
      }
    }
    flashMessages();
    highlightSidebar();
    convertMarkdown();
    checkTimezone();

    // Instantiate top navigation bar dropdowns and tooltips
    $('nav#topbar button.dropdown-button').dropdown();
    $('nav#topbar a[data-toggle="tooltip"]').tooltip();

    $('li.expanded-toggle a').on('click', toggleSidebar);
    if ($(window).width() <= 767 && $('nav#sidebar').height() === 300) {
      toggleSidebar();
    }

    // eslint-disable-next-line
    const sessionSec = new SessionSecurity({
      pingUrl: $('input[name=session-security-ping-url]').val(),
      warnAfter: $('input[name=session-security-warn-after]').val(),
      expireAfter: $('input[name=session-security-expire-after]').val(),
      confirmFormDiscard: 'You have unsaved changes in a form of this page.',
    });
  }

  return { ...authState, isVanityUrlLogin };
}

export function initializeBasePage() {
  flashMessages();
}
